import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import FiskLogo from "@/components/FiskLogo";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="h-16 bg-black flex items-center px-4 sm:px-8">
        <FiskLogo to="/login" />
      </div>
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md space-y-6" data-testid="forgot-password-page">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-[#E30613] font-medium"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="w-3 h-3" /> Voltar ao login
          </Link>

          {!sent ? (
            <>
              <div>
                <h2 className="text-3xl font-bold text-neutral-900">Esqueci minha senha</h2>
                <p className="text-sm text-neutral-500 mt-2">
                  Informe o e-mail cadastrado. Se ele existir, enviaremos um link seguro para você criar uma nova senha.
                </p>
              </div>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-password-form">
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    data-testid="forgot-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    className="rounded-sm"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                  className="w-full bg-[#E30613] hover:bg-[#B3040E] text-white rounded-sm h-11 font-semibold"
                >
                  {loading ? "Enviando..." : "Enviar link de redefinição"}
                </Button>
              </form>
            </>
          ) : (
            <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-6" data-testid="forgot-sent-confirmation">
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mb-3" />
              <h2 className="text-2xl font-bold text-neutral-900">Verifique seu e-mail</h2>
              <p className="text-sm text-neutral-700 mt-3 leading-relaxed">
                Se <strong className="text-neutral-900">{email}</strong> estiver cadastrado, você receberá um e-mail em instantes com um link seguro para criar uma nova senha.
              </p>
              <p className="text-xs text-neutral-500 mt-4 leading-relaxed">
                O link vale por <strong>1 hora</strong>. Não se esqueça de verificar a caixa de spam / lixo eletrônico se não encontrar na entrada.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={() => { setSent(false); setEmail(""); }}
                  variant="outline"
                  className="rounded-sm"
                  data-testid="send-again-btn"
                >
                  Enviar novamente
                </Button>
                <Link to="/login">
                  <Button className="bg-black hover:bg-neutral-800 text-white rounded-sm w-full sm:w-auto">
                    Voltar ao login
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
